/**
 * Reduced-motion entrance gate for onboarding (proposal §10).
 *
 * The onboarding flow leans on reanimated entering animations (`FadeInDown`,
 * `FadeInUp`, `ZoomIn`, …). WCAG 2.2 + `prefers-reduced-motion` require those to
 * be suppressed for viewers who ask for reduced motion. Reanimated v4 already
 * defaults entering builders to `ReduceMotion.System`, but we gate them
 * EXPLICITLY here so the behaviour is guaranteed, testable, and independent of
 * that implicit default.
 *
 * Usage:
 *   const entrance = useEntrance();
 *   <Animated.View entering={entrance(FadeInDown.delay(100))}>…
 *
 * When reduced motion is on, `entrance(builder)` returns `undefined`, so the
 * view mounts with no animation; otherwise the builder passes through unchanged.
 */
import { useCallback } from 'react';
import type { ComponentProps } from 'react';
import Animated, { useReducedMotion } from 'react-native-reanimated';

/** The `entering` prop type accepted by an `Animated.*` component. */
type EnteringProp = NonNullable<ComponentProps<typeof Animated.View>['entering']>;

/**
 * Pure decision behind {@link useEntrance}: drop the animation builder when the
 * viewer prefers reduced motion (or when there is no builder), otherwise return
 * it unchanged.
 */
export function resolveEntrance<T extends EnteringProp>(
  reduced: boolean,
  builder: T | undefined,
): T | undefined {
  return reduced || !builder ? undefined : builder;
}

/**
 * Hook returning an `entrance()` helper bound to the current reduced-motion
 * preference. Wrap every onboarding `entering={…}` builder in it.
 */
export function useEntrance(): <T extends EnteringProp>(builder: T) => T | undefined {
  const reduced = useReducedMotion();
  return useCallback(
    <T extends EnteringProp>(builder: T): T | undefined => resolveEntrance(reduced, builder),
    [reduced],
  );
}
